import { useClientLocale, useClientTranslation } from '@bts-web/utils-lokalise';
import { Drawer, SearchBar } from '../../../../common';
import {
  AssetItemSkeleton,
  AssetsListSkeleton,
} from '../../../../prices/AssetsDiscoverPageSkeleton/AssetsDiscoverPageSkeleton';
import { useStakeAssetsData } from '../../../hooks/useStakeAssetsData';
import { getStakeTranslations } from '../../../utils/getStakeTranslations';
import { css } from '@bts-web/utils-style-engine';
import { TransferEntityPickerEntry } from '../../../../crypto-transfers/components/Reusable/TransferEntityPickerEntry/TransferEntityPickerEntry';
import { useRouter } from 'next/navigation';
import { NEXT_ROUTES } from '@bts-web/utils-next-infrastructure';
import { ContentSectionWrapper } from '../../../../layouts/ContentSectionWrapper/ContentSectionWrapper';
import { FilledDismiss, FilledSearch } from '../../../../common/svgIcons';
import {
  ASSETS_LIST_PAGE_SIZE,
  MAXIMUM_SEARCH_TERM_LENGTH,
} from '../../../../config';
import {
  useDrawerMetadata,
  useModalDrawerContext,
} from '../../../../common/components/ModalDrawerContext/ModalDrawerContext';
import {
  InfiniteScroll,
  InfiniteScrollItem,
} from '@bts-web/design-system/component/infinite-scroll';
import { intlFiatValueFormatting } from '@bts-web/utils-formatting';
import { getAppConfig } from '@bts-web/core-app-config';
import { UnownedAssetDrawer } from '../UnownedAssetDrawer/UnownedAssetDrawer';
import { AssetListSection } from './AssetListSection';

const StakingAssetSelectDrawer = ({
  isUnstaking,
}: {
  isUnstaking?: boolean;
}) => {
  const { t } = useClientTranslation();

  const { closeThisDrawer } = useDrawerMetadata();

  const { openDrawer } = useModalDrawerContext();

  const { push } = useRouter();

  const { currency } = getAppConfig();

  const stakingTranslations = getStakeTranslations(t);

  const locale = useClientLocale();

  const {
    loading,
    response,
    searchTerm,
    setSearchTerm,
    clearSearch,
    loadMoreAssets,
  } = useStakeAssetsData();

  const { ownedAssets, otherAssets } = response?.data ?? {};

  const excludeFromOtherAssets =
    ownedAssets?.assets?.edges?.map((item) => item?.node?.id) || [];

  const onlyOtherAssets = otherAssets?.edges?.filter(
    (edge) => !excludeFromOtherAssets.includes(edge?.node.id as string),
  );

  return (
    <Drawer title="Select Asset">
      <ContentSectionWrapper
        className={css({
          display: 'flex',
          flexDirection: 'column',
          gap: 'small',
        })}
      >
        <SearchBar
          value={searchTerm}
          onChange={setSearchTerm}
          onClear={clearSearch}
          placeholder={t('search_assets')}
          maxLength={MAXIMUM_SEARCH_TERM_LENGTH}
          searchIcon={<FilledSearch size={24} />}
          dismissIcon={<FilledDismiss />}
        />

        {loading && <AssetsListSkeleton />}

        {!loading &&
        ownedAssets?.assets &&
        (ownedAssets.assets.edges?.length ?? 0 > 0) ? (
          <AssetListSection title={stakingTranslations.yourAssets}>
            {ownedAssets.assets.edges?.map((asset) => {
              const availableBalance = intlFiatValueFormatting(
                Number(asset?.fiatBalance?.value || 0) -
                  Number(asset?.fiatTotalStakedAmount?.value || 0),
                {
                  locale,
                  currency,
                  fractionDigits: asset?.fiatBalance?.precision,
                },
              );

              const hasFundsForStaking =
                Number(asset?.fiatBalance?.value || '0') > 0;

              const canStake = asset?.node?.staking?.enabled ?? false;

              return (
                <TransferEntityPickerEntry
                  withinList
                  title={asset?.node?.name ?? 'N/A'}
                  subtitle={t(stakingTranslations.amountAvailable, {
                    amount: availableBalance,
                  })}
                  active={false}
                  type="entryWithActiveState"
                  imageUrl={asset?.node?.logoUrl}
                  endInfoMain={
                    asset?.node?.staking?.yearlyInterest
                      ? `${asset.node?.staking?.yearlyInterest}%`
                      : 'N/A'
                  }
                  endInfoSub={stakingTranslations.perYear}
                  disabled={!canStake}
                  onPick={() => {
                    closeThisDrawer();

                    const operation = isUnstaking ? 'unstake' : 'stake';

                    if (asset?.node?.id) {
                      if (hasFundsForStaking) {
                        push(
                          `${NEXT_ROUTES.ASSET_DETAILS.replace('[id]', asset.node.id)}/${operation}`,
                        );
                      } else {
                        openDrawer(UnownedAssetDrawer, {
                          assetData: {
                            id: asset?.node?.id,
                            logoUrl: asset?.node?.logoUrl as string,
                            name: asset?.node?.name as string,
                          },
                          position: 'bottom',
                        });
                      }
                    }
                  }}
                  key={asset?.node?.id}
                />
              );
            })}
          </AssetListSection>
        ) : null}
        {!loading && onlyOtherAssets && onlyOtherAssets.length > 0 && (
          <AssetListSection title={stakingTranslations.otherAssets}>
            <InfiniteScroll
              itemsPerPage={ASSETS_LIST_PAGE_SIZE}
              fetchMore={loadMoreAssets}
              hasMore={response?.data?.otherAssets?.pageInfo?.hasNextPage}
              skeleton={<AssetItemSkeleton />}
              items={
                onlyOtherAssets?.map((e) => e?.node) as InfiniteScrollItem[]
              }
              renderItemComponent={(itemData) => {
                const canStake = itemData.staking?.enabled ?? false;

                return (
                  <TransferEntityPickerEntry
                    withinList
                    title={itemData.name ?? 'N/A'}
                    active={false}
                    type="entryWithActiveState"
                    imageUrl={itemData.logoUrl}
                    endInfoMain={
                      itemData.staking?.yearlyInterest
                        ? `${itemData.staking.yearlyInterest}%`
                        : 'N/A'
                    }
                    endInfoSub={stakingTranslations.perYear}
                    disabled={!canStake}
                    onPick={() => {
                      closeThisDrawer();

                      openDrawer(UnownedAssetDrawer, {
                        assetData: {
                          id: itemData.id,
                          logoUrl: itemData.logoUrl,
                          name: itemData.name,
                        },
                        position: 'bottom',
                      });
                    }}
                    key={itemData.id}
                  />
                );
              }}
            />
          </AssetListSection>
        )}
      </ContentSectionWrapper>
    </Drawer>
  );
};

export { StakingAssetSelectDrawer };
